import { Box } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { useParams } from "react-router";

import { FeaturingAppsSection } from "./featuring-apps-section";
import { QuestDetailsMainCard, QuestDetailsMainCardSkeleton } from "./quest-details-main-card";
import { QuestRewardCard } from "./QuestRewardCard";
import { TaskCard } from "./TaskCard";

import { NavButton } from "@/components/nav-button";
import { PageLayout } from "@/components/page-layout";
import { AppRoute, LATEST_QUEST_BANNER_ID, QueryKey } from "@/constants/shared";
import { useAuth } from "@/hooks/useAuth";
import { useHomeBanner } from "@/hooks/useHomeBanner";
import { makeRequest } from "@/network/api";
import { Quest, Task } from "@/types";

export const QuestDetails = () => {
  const { address, isAuthenticated } = useAuth();
  const { questId } = useParams();

  const unescapeText = (text: string) => {
    // Unescape newline characters from backend
    return text.replace(/\\n/g, "\n");
  };

  const { data: quest } = useQuery({
    queryKey: [QueryKey.LatestQuest, questId, address],
    queryFn: async (): Promise<Quest> => {
      const response = await makeRequest(`quests/${questId}`);
      const responseJson = await response.json();
      const questData = responseJson.data.quest;
      return {
        ...questData,
        description: questData.description ? unescapeText(questData.description) : questData.description,
      };
    },
  });

  const { dismissBannerId } = useHomeBanner();

  useEffect(() => {
    dismissBannerId(LATEST_QUEST_BANNER_ID);
  }, [dismissBannerId]);

  return (
    <PageLayout header={null}>
      <NavButton direction="bk" href={AppRoute.Quests} h={8} w={12} bgColor="bg" />
      {!quest ? (
        <QuestDetailsMainCardSkeleton />
      ) : (
        <>
          <QuestDetailsMainCard quest={quest} />
          <Box pb={6} borderBottom="1px solid #F0F0F0" w="full">
            <FeaturingAppsSection tasks={quest.tasks.filter((task) => !!task.relatedApp)} />
          </Box>
          {quest.tasks
            .sort((a, b) => a.position - b.position)
            .map((task: Task, idx: number) => (
              <TaskCard key={task.id} task={task} isAuthenticated={isAuthenticated} idx={idx} />
            ))}
          <QuestRewardCard quest={quest} />
        </>
      )}
    </PageLayout>
  );
};
